// covered-by: frontends/carrie/src/ask-v2/services/__tests__/postToAskV2ProjectSharedFeed.test.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  sendAskV2Message,
  type AskV2Attachment,
  type AskV2ChatAck,
  type AskV2QuotedContext,
} from "./chat";

import { useUI } from "../../contexts/ui";
import { getProject } from "../../services/askPunttProjects";
import { askPunttKeys } from "../../utils/queries/askPuntt";
import { askPunttProjectKeys } from "../../utils/queries/askPunttProjects";

export type PostToAskV2ProjectSharedFeedInput = {
  projectId: string;
  sharedChatId?: string | null;
  message: string;
  attachments?: AskV2Attachment[];
  quotedContexts?: AskV2QuotedContext[];
};

export type PostToAskV2ProjectSharedFeedResult = {
  sharedChatId: string;
  ack: AskV2ChatAck;
};

/**
 * Home Project-mode submit for Warhol: append a turn onto the project's
 * shared feed chat. Never creates a personal chat.
 */
export async function postToAskV2ProjectSharedFeed(
  input: PostToAskV2ProjectSharedFeedInput,
): Promise<PostToAskV2ProjectSharedFeedResult> {
  let sharedChatId =
    input.sharedChatId != null && input.sharedChatId !== ""
      ? input.sharedChatId
      : null;
  if (sharedChatId == null) {
    const projectResult = await getProject(input.projectId);
    const resolved = projectResult.project.sharedChatId;
    sharedChatId = resolved != null && resolved !== "" ? resolved : null;
  }
  if (sharedChatId == null) {
    throw new Error(`Project ${input.projectId} has no shared feed chat`);
  }

  const { message, attachments, quotedContexts } = input;
  const ack =
    attachments?.length && quotedContexts?.length
      ? await sendAskV2Message(
          sharedChatId,
          message,
          attachments,
          quotedContexts,
        )
      : attachments?.length
        ? await sendAskV2Message(sharedChatId, message, attachments)
        : quotedContexts?.length
          ? await sendAskV2Message(
              sharedChatId,
              message,
              undefined,
              quotedContexts,
            )
          : await sendAskV2Message(sharedChatId, message);
  return { sharedChatId, ack };
}

/** Home Project-mode submit with cache refresh and V1-parity error toast. */
export function usePostToAskV2ProjectSharedFeed() {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: postToAskV2ProjectSharedFeed,
    onSuccess: (data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: askPunttKeys.messages(data.sharedChatId),
      });
      void queryClient.invalidateQueries({
        queryKey: askPunttKeys.chat(data.sharedChatId),
      });
      void queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.feed(vars.projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.timeline(vars.projectId),
      });
    },
    onError: (error) => {
      console.error("Failed to post to project shared feed:", error);
      notify({
        title: "Failed to post",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}
